<script lang="ts" setup>
import Dialog from 'primevue/dialog'
import { dialog as dialogStyle } from '~/utils/primeStyles/dialog.ts'

const posCheckStore = usePosCheckStore()
const {
  comboCustomized,
  comboModal,
  comboStaged,
  comboStagedTotal,
  comboCustomOptions,
} = storeToRefs(posCheckStore)

function resetComboState() {
  comboStaged.value = {}
  comboCustomOptions.value = null
  comboModal.value = false
}

function wrapperUpdateComboInCart() {
  posCheckStore.updateComboInCheckCart(comboStaged.value)
  comboModal.value = false
}

function cancelComboCustomization() {
  comboCustomized.value = false
  comboModal.value = false
}

// Handle both add and update cases
function updateCustomComboWrapper() {
  posCheckStore.updateCustomComboInCheckCart()
  comboModal.value = false
}

const headerTitle = computed(() => {
  if (!comboStaged.value?.name) {
    return 'Combo details'
  }
  return comboStaged.value.name
})

// Get default modifiers for a product in the combo
function getDefaultModifiersForProduct(productId: number) {
  if (!comboStaged.value?.items) return []

  // Find the group that contains this product
  for (const group of comboStaged.value.items) {
    const product = group.options?.find((opt: any) => opt.product_id === productId && opt.default)
    if (product?.product_modifiers) {
      // Get default modifiers
      const defaultModifiers: any[] = []
      product.product_modifiers.forEach((modifier: any) => {
        if (modifier.default_option?.length > 0) {
          modifier.default_option.forEach((defaultOpt: any) => {
            defaultModifiers.push({
              id: defaultOpt.id,
              name: defaultOpt.name,
            })
          })
        }
      })
      return defaultModifiers
    }
  }
  return []
}
</script>

<template>
  <Dialog
    v-model:visible="comboModal"
    modal
    :header="headerTitle"
    :pt="dialogStyle"
    position="top"
    @hide="resetComboState"
  >
    <div class="h-full">
      <div
        class="lg:grid lg:h-full"
        :class="comboCustomized ? 'lg:grid-cols-10' : ''"
      >
        <!-- Customization Panel (only shown when customized) -->
        <div
          class="customization"
          :class="{ 'p-2 shadow-xl overflow-y-scroll col-span-5': comboCustomized, 'hidden': !comboCustomized }"
        >
          <POSEditCheckCustomFormNative v-if="comboCustomized" />
        </div>
        <!-- Summary Panel -->
        <div
          id="product-details"
          class="relative"
          :class="{ 'col-span-5': comboCustomized }"
        >
          <div class="flex flex-col">
            <div class="min-h-24 bg-gray-500" />
            <div
              id="item-photo"
              class="absolute left-1/2 top-12 mr-2 size-24 -translate-x-1/2 overflow-hidden rounded-full border-2 border-gray-700 shadow-lg"
            >
              <img
                :src="getProductFirstImage(comboStaged)"
                class="w-24"
                :alt="`product: ${comboStaged?.name}`"
              >
            </div>
            <div class="pt-8 text-center font-sans flex-grow flex flex-col justify-end">
              <h2 class="mb-1 mt-8 font-normal">
                {{ comboStaged?.name }}
              </h2>
              <div class="font-semibold">
                <span>P/U: </span>{{ usePriceFormatter(comboStaged?.base_price) }}
              </div>
              <div class="mb-5 text-2xl font-bold">
                <span v-if="!comboCustomized">
                  Total: {{ usePriceFormatter(comboStagedTotal) }}
                </span>
                <span v-else-if="comboCustomOptions">
                  Total: {{ usePriceFormatter(comboCustomOptions?.total) }}
                </span>
              </div>
              <div v-if="!comboCustomized">
                <ul class="list-none">
                  <li
                    v-for="item in comboStaged?.defaults"
                    :key="item.product"
                    class="mb-2"
                  >
                    <div>
                      <span>{{ item.quantity }} x </span>
                      <span>{{ item.product_name }}</span>
                    </div>
                    <!-- Show default modifiers -->
                    <div
                      v-if="getDefaultModifiersForProduct(item.product_id)"
                      class="pl-4 text-sm text-gray-600"
                    >
                      <div
                        v-for="modifier in getDefaultModifiersForProduct(item.product_id)"
                        :key="modifier.id"
                      >
                        + {{ modifier.name }}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
              <div>
                <ul
                  v-if="comboCustomized"
                  class="list-none px-6"
                >
                  <li
                    v-for="item in comboCustomOptions?.selected"
                    :key="item?.id"
                  >
                    <div class="px-3 text-left text-lg font-semibold">
                      <span v-if="item?.quantity">{{ item?.quantity }} x </span>
                      <span>{{ item?.product_name }}</span>
                      <span
                        v-if="item?.price_variation && item?.price_variation > 0"
                        class="text-black ml-2"
                      >
                        + {{ usePriceFormatter(item?.price_variation) }}
                      </span>
                    </div>

                    <!-- Show unified modifier selections -->
                    <div
                      v-if="item?.selected_modifiers"
                      class="text-left pl-4"
                    >
                      <template
                        v-for="(selections, modifierId) in item.selected_modifiers"
                        :key="modifierId"
                      >
                        <div
                          v-if="Array.isArray(selections) && selections.length > 0"
                          class="pl-4"
                        >
                          <div
                            v-for="selection in selections"
                            :key="selection.id"
                            class="flex justify-between"
                          >
                            <span>+ {{ selection.name }}</span>
                            <span v-if="selection.price_modifier > 0"> - {{ usePriceFormatter(selection.price_modifier) }}<span v-if="comboCustomOptions?.type === 'CUSTOM_COMBO' && item?.quantity > 1"> x {{ item.quantity }} = {{ usePriceFormatter(selection.price_modifier * item.quantity) }}</span></span>
                          </div>
                        </div>
                      </template>
                    </div>
                  </li>
                </ul>
              </div>
              <div class="flex items-center justify-between gap-2 bg-stone-200 px-3 py-2">
                <div>
                  <Button
                    v-if="!comboCustomized"
                    :label="$t('Customize combo')"
                    icon="pi pi-pencil"
                    severity="warn"
                    @click="posCheckStore.customizeCombo"
                  />
                  <Button
                    v-else-if="comboCustomized"
                    icon="pi pi-times"
                    severity="warn"
                    :label="$t('Cancel combo customization')"
                    @click="cancelComboCustomization"
                  />
                </div>
                <div
                  class="flex items-center justify-center"
                >
                  <Button
                    class="rounded-full"
                    icon="pi pi-minus"
                    aria-lable="Remove one"
                    severity="danger"
                    @click="posCheckStore.decreaseComboQuantityStaged"
                  />
                  <div v-if="comboStaged?.quantity">
                    <span class="mx-1 font-serif text-3xl md:mx-4">{{ comboStaged.quantity }}</span>
                  </div>
                  <Button
                    class="rounded-full"
                    icon="pi pi-plus"
                    aria-lable="Add one"
                    severity="success"
                    @click="posCheckStore.increaseComboQuantityStaged"
                  />
                </div>
                <div class="text-center">
                  <!-- Update button for non-custom combos -->
                  <Button
                    v-if="!comboCustomized"
                    :label="$t('Update combo')"
                    icon="pi pi-check"
                    @click="wrapperUpdateComboInCart"
                  />
                  <!-- Update button for custom combos -->
                  <Button
                    v-if="comboCustomOptions"
                    :disabled="comboCustomOptions?.itemsNeeded !== 0 || !posCheckStore.validateRequiredModifiers()"
                    icon="pi pi-check"
                    :label="$t('Update combo')"
                    severity="success"
                    @click="updateCustomComboWrapper"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
